import { faClipboardCheck, faLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Space } from 'antd';
import type { ColumnType } from 'antd/lib/table';
import { FormattedMessage } from 'react-intl';

import { api } from '@/api';
import { AddAction, AttachmentAction, DeleteAction, EditAction } from '@/components/table/actions';
import {
  PERMISSION_CREATE,
  PERMISSION_DELETE,
  PERMISSION_OBJ_WAYBILL,
  PERMISSION_OBJ_LOT,
  PERMISSION_UPDATE,
} from '@/constants/permissions';
import { withPermissions } from '@/hocs/permission';
import { waybillStore } from '@/stores';
import { ProductType } from '@/types/enums';
import { productFormattedLabel, showError } from '@/utils/common';
import { getFormattedDate } from '@/utils/moment';
import { WaybillIcon } from '@/components/icons';
import { getColumnSearchFields } from '@/components/table/filters';

/** Waybill list filter by `products.type` (DB enum product_type); labels match products table i18n */
const productTypeFilterOptions = Object.values(ProductType).map((value) => ({
  value,
  text: <FormattedMessage id={`products.type.${value}`} />,
}));

const PermissionedEditAction = withPermissions([[PERMISSION_OBJ_WAYBILL, PERMISSION_UPDATE]])(EditAction);
const PermissionedCreateLotAction = withPermissions([[PERMISSION_OBJ_LOT, PERMISSION_CREATE]])(AddAction);
const PermissionedAttachLotAction = withPermissions([
  [PERMISSION_OBJ_LOT, PERMISSION_UPDATE],
  [PERMISSION_OBJ_WAYBILL, PERMISSION_UPDATE],
])(AddAction);
const PermissionedDeleteAction = withPermissions([[PERMISSION_OBJ_WAYBILL, PERMISSION_DELETE]])(DeleteAction);
const PermissionedAttachmentAction = withPermissions([[PERMISSION_OBJ_WAYBILL, PERMISSION_UPDATE]])(AttachmentAction);

const Actions = (_: any, record: any) => {
  const onDelete = () => {
    api.waybills
      .delete(record.id)
      .source.then(() => waybillStore.deleteItem(record.id))
      .catch(showError);
  };

  // Block row-level onClick (which navigates to detail view) — the actions cell
  // is its own interactive region. Without this, clicking Delete fires the row
  // navigation in parallel and the detail view tries to load a record that's
  // about to disappear.
  return (
    <Space onClick={(e) => e.stopPropagation()}>
      <PermissionedEditAction uiSref={{ to: 'base-layout.waybills.edit', params: { waybillId: record.id } }} />
      <PermissionedCreateLotAction
        uiSref={{ to: 'base-layout.waybills.lot', params: { waybillId: record.id } }}
        title="Lot"
        icon={<FontAwesomeIcon icon={faClipboardCheck} />}
        tooltipTitle="waybill.addLot"
      />
      <PermissionedAttachLotAction
        uiSref={{ to: 'base-layout.waybills.attach-lot', params: { waybillId: record.id } }}
        title="Attach"
        icon={<FontAwesomeIcon icon={faLink} />}
        tooltipTitle="waybill.attachLot"
      />
      <PermissionedAttachmentAction
        uiSref={{ to: 'base-layout.waybills.attachments', params: { waybillId: record.id } }}
      />
      <PermissionedDeleteAction onConfirm={onDelete} />
    </Space>
  );
};

const getLatestAnalysis = (record: cyrian.api.waybills.Waybill) => {
  const arr = record.weighingAnalyses;
  return arr?.length ? arr[0] : undefined;
};

/** Optional key override for getGenericFilters (avoid dotted query params broken by Express qs) */
type WaybillColumn = ColumnType<cyrian.api.waybills.Waybill> & { filterQueryParam?: string };

export const columns: WaybillColumn[] = [
  {
    dataIndex: ['product', 'name'],
    sorter: false,
    width: 50,
    render: (_, record) => {
      const cooperative = record.cooperative ?? getLatestAnalysis(record)?.cooperative;
      return <WaybillIcon type={record?.product?.type} isEudrCompliant={cooperative?.isEudrCompliant} />;
    },
  },
  {
    title: 'id',
    dataIndex: 'id',
    defaultSortOrder: 'descend',
    sorter: true,
  },
  {
    title: <FormattedMessage id="waybills.product" />,
    dataIndex: ['product', 'type'],
    /** Backend query param must be productType (Express qs nests product.type) */
    filterQueryParam: 'productType',
    /** Single-select: getGenericFilters CSV-joins multi-values but the enum filter compares exact, so 2+ never match */
    filterMultiple: false,
    filters: productTypeFilterOptions,
    sorter: true,
    render: (_, record) => productFormattedLabel(record.product),
  },
  {
    title: <FormattedMessage id="waybills.campaign" />,
    dataIndex: ['campaign', 'name'],
    sorter: true,
  },
  {
    title: <FormattedMessage id="waybills.latestSupplierName" />, // Title "Cooperative" in table
    dataIndex: ['cooperative', 'name'],
    ...getColumnSearchFields(),
    render: (_, record) => record.cooperative?.name ?? getLatestAnalysis(record)?.cooperative?.name,
  },
  {
    title: <FormattedMessage id="waybills.cooperativeConsortiums" />,
    // Filter key must match WaybillQueryDto / waybill.mapper (cooperative → consortium).
    // Does not match consortiums shown only via latest weighing analysis — see waybill list query joins.
    dataIndex: ['cooperativeConsortium', 'name'],
    ...getColumnSearchFields(),
    sorter: false,
    render: (_, record) => {
      // Waybill first, then latest analysis (same priority as cooperative name/code)
      const cooperative = record.cooperative ?? getLatestAnalysis(record)?.cooperative;
      const fromDetails = cooperative?.consortiumDetails
        ?.map((cd) => cd.consortium?.name)
        .filter(Boolean)
        .join(', ');
      const fromConsortiums = cooperative?.consortiums?.length
        ? cooperative.consortiums.map((c) => c.name).join(', ')
        : null;
      return fromDetails || fromConsortiums || null;
    },
  },
  {
    title: <FormattedMessage id="waybills.latestSupplierCode" />,
    dataIndex: ['cooperative', 'code'],
    ...getColumnSearchFields(),
    render: (_, record) => record.cooperative?.code ?? getLatestAnalysis(record)?.cooperative?.code,
  },
  {
    title: <FormattedMessage id="waybills.exporter" />,
    dataIndex: ['exporter', 'name'],
    ...getColumnSearchFields(),
    render: (_, record) => record.exporter?.name ?? getLatestAnalysis(record)?.exporter?.name,
  },
  {
    title: <FormattedMessage id="waybills.waybillNumber" />,
    dataIndex: 'waybillNumber',
    ...getColumnSearchFields(),
    sorter: true,
  },
  {
    title: <FormattedMessage id="waybills.latestWeightingCode" />,
    dataIndex: 'latestWeighingCode',
    sorter: false,
    render: (_, record) => getLatestAnalysis(record)?.weighingCode,
  },

  {
    title: <FormattedMessage id="waybills.latestOriginDepartment" />,
    dataIndex: 'latestOriginDepartment',
    ...getColumnSearchFields(),
    // sorter: true,
    render: (_, record) => getLatestAnalysis(record)?.originDepartment,
  },
  {
    title: <FormattedMessage id="waybills.factory" />,
    dataIndex: 'factory',
    ...getColumnSearchFields(),
    // sorter: true,
    render: (_, record) => record.factory?.name,
  },
  {
    title: <FormattedMessage id="waybills.harbor" />,
    dataIndex: ['harbor', 'name'],
    ...getColumnSearchFields(),
    render: (_, record) => record.harbor?.name,
  },
  {
    title: <FormattedMessage id="waybills.latestAnalyzedAt" />,
    dataIndex: 'latestAnalyzedAt',
    sorter: true,
    render: (_, record) => getFormattedDate(getLatestAnalysis(record)?.analysisDate as string | Date | undefined),
  },
  {
    title: <FormattedMessage id="waybills.productNature" />,
    dataIndex: 'productNature',
    sorter: true,
  },
  {
    title: <FormattedMessage id="waybills.latestNatGrade" />,
    dataIndex: 'latestNatGrade',
    sorter: false,
    render: (_, record) => getLatestAnalysis(record)?.natGrade,
  },
  {
    title: <FormattedMessage id="waybills.latestFccGrade" />,
    dataIndex: 'latestFccGrade',
    sorter: false,
    render: (_, record) => getLatestAnalysis(record)?.fccGrade,
  },

  // {
  //   title: <FormattedMessage id="waybills.analysisQualityGrade" />,
  //   dataIndex: "analysisQualityGrade",
  //   sorter: true,
  // },
  // {
  //   title: <FormattedMessage id="waybills.lotNumber" />,
  //   dataIndex: ["lot", "number"],
  //   sorter: true,
  // },

  {
    title: <FormattedMessage id="waybills.latestBags" />,
    dataIndex: 'latestBags',
    sorter: false,
    render: (_, record) => getLatestAnalysis(record)?.bagsCount,
  },
  {
    title: <FormattedMessage id="waybills.declaredNetWeightKg" />,
    // Comes from the PDF waybill, set during ingestion. Distinct from
    // acceptedWeight (which is the reception weighing measurement) — both are
    // displayed side-by-side so the operator can compare declared vs accepted.
    dataIndex: 'declaredNetWeightKg',
    sorter: true,
  },
  {
    title: <FormattedMessage id="waybills.latestAcceptedWeight" />,
    dataIndex: 'latestAcceptedWeight',
    sorter: true,
    render: (_, record) => getLatestAnalysis(record)?.acceptedWeight,
  },
  {
    title: <FormattedMessage id="waybills.latestCccExportDatetime" />,
    dataIndex: 'latestCccExportDatetime',
    sorter: true,
    render: (_, record) => getLatestAnalysis(record)?.cccExportDatetime,
  },
  {
    title: <FormattedMessage id="waybills.status" />,
    dataIndex: 'status',
    sorter: true,
  },
  {
    title: '',
    render: Actions,
    className: 'actions',
  },
];
